import React, {
  useState,
  useEffect,
  useDeferredValue,
  useMemo,
  ChangeEvent,
} from "react";
import Link from "next/link";
import Router from "next/router";
import {
  Input,
  FormGroup,
  Container,
  Table,
  Row,
  Col,
  Badge,
} from "reactstrap";
import { DownloadIcon } from "@primer/octicons-react";
import { stringify } from "csv-stringify/sync";

import useApi from "~/hooks/use-api";
import VersionBadge from "~/components/VersionBadge";
import WarningPopover from "~/components/WarningsPopover";
import { ClipboardLabel } from "~/components/ClipboardLabel";
import {
  compareOrganizationNames,
  matchesOrganizationSearch,
} from "~/utils/orgs-table";

export interface Organization {
  uid: string;
  name: string;
  timezone: string;
  currency: string;
  disabled: string;
  accountManager: string | null;
  stripeAccountId: string | null;
}

interface AppStats {
  orgId: string;
  iosAppId: string;
  iosAppVersion?: string;
  androidAppId: string;
  androidAppVersion?: string;
}

type SortType =
  | "name-asc"
  | "name-desc"
  | "android-asc"
  | "android-desc"
  | "ios-asc"
  | "ios-desc";

const nameSortHeaders: Partial<
  Record<SortType, { label: string; ariaSort: "ascending" | "descending" }>
> = {
  "name-asc": { label: "A–Z", ariaSort: "ascending" },
  "name-desc": { label: "Z–A", ariaSort: "descending" },
};

// App Versions badge cycle; after iOS descending it returns to name order.
const nextVersionSort: Record<SortType, SortType> = {
  "name-asc": "android-asc",
  "name-desc": "android-asc",
  "android-asc": "android-desc",
  "android-desc": "ios-asc",
  "ios-asc": "ios-desc",
  "ios-desc": "name-asc",
};

const regionName =
  typeof window === "object"
    ? /operator\.([a-z]+)\.loke\.global/.exec(location.hostname)?.[1] ?? null
    : null;

export default function OrgsIndex() {
  const [filterSearch, setFilterSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("enabled");
  const [filterAcctMgr, setFilterAcctMgr] = useState("");
  const [sort, setSort] = useState<SortType>("name-asc");
  const nameSortHeader = nameSortHeaders[sort];
  const { data: appstats } = useApi<AppStats[]>(`/api/appstats`);

  const orgVersions = useMemo(() => {
    if (!appstats) return new Map<string, AppStats>();
    return new Map(appstats.map((stat) => [stat.orgId, stat]));
  }, [appstats]);

  const { data: orgs } = useApi<Organization[]>(
    "/api/organization?includeDisabled=true"
  );

  const csvUrl = useCSVBlobURL(orgs);
  const csvFileName = regionName
    ? `organizations-${regionName}.csv`
    : "organizations.csv";

  const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) =>
    setFilterSearch(event.target.value);

  const visibleOrgs = useMemo(() => {
    if (!orgs) return [];

    return orgs
      .filter((org) => {
        if (!matchesOrganizationSearch(org, filterSearch)) return false;
        if (
          filterStatus &&
          (org.disabled ? "disabled" : "enabled") !== filterStatus
        ) {
          return false;
        }
        if (!filterAcctMgr) return true;
        if (filterAcctMgr === "none") return !org.accountManager;
        return org.accountManager === filterAcctMgr;
      })
      .sort((a, b) => compareOrganizations(a, b, sort, orgVersions));
  }, [orgs, filterSearch, filterStatus, filterAcctMgr, sort, orgVersions]);

  const handleShowDisable = (event: ChangeEvent<HTMLInputElement>) =>
    setFilterStatus(event.target.value);

  const handleSelectAcctMgr = (event: ChangeEvent<HTMLInputElement>) =>
    setFilterAcctMgr(event.target.value);

  const accountManagers: string[] = orgs
    ? orgs
        .map((org) => org.accountManager)
        .filter((email): email is string => Boolean(email))
        .filter((email, index, arr) => arr.indexOf(email) === index) // remove duplicates
        .sort()
    : [];

  return (
    <Container>
      <Row form className="mt-4">
        <Col lg="5">
          <FormGroup>
            <Input
              type="search"
              placeholder="Find by name, currency or ID..."
              aria-label="Search organizations by name, currency or ID"
              value={filterSearch}
              onChange={handleFilterChange}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  !event.nativeEvent.isComposing &&
                  // Safari reports isComposing false for the Enter that commits IME text.
                  event.nativeEvent.keyCode !== 229 &&
                  visibleOrgs.length === 1
                ) {
                  Router.push("/orgs/[org]", `/orgs/${visibleOrgs[0].uid}`);
                }
              }}
              autoFocus={true}
            />
          </FormGroup>
        </Col>
        <Col sm="6" md="4" lg="2">
          <FormGroup>
            <Input
              type="select"
              name="select"
              id="orgAcctMgr"
              onChange={handleSelectAcctMgr}
              defaultValue=""
            >
              <option value="">Any account manager</option>
              <option value="none">No account manager</option>
              {accountManagers.map((email) => (
                <option key={email} value={email}>
                  {email}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col sm="6" md="4" lg="2">
          <FormGroup>
            <Input
              type="select"
              name="select"
              id="orgStatus"
              onChange={handleShowDisable}
              defaultValue="enabled"
            >
              <option value="">All</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </Input>
          </FormGroup>
        </Col>
        <Col md="4" lg="3">
          <FormGroup className="d-flex justify-content-between">
            {csvUrl && (
              <a
                className="btn btn-outline-secondary"
                href={csvUrl}
                download={csvFileName}
              >
                <DownloadIcon size={16} />
              </a>
            )}
            <Link href="/orgs/new">
              <a className="btn btn-primary">New Organization</a>
            </Link>
          </FormGroup>
        </Col>
      </Row>
      <Table>
        <thead>
          <tr>
            <th aria-sort={nameSortHeader?.ariaSort ?? "none"}>
              Name{" "}
              <button
                type="button"
                className="badge badge-primary border-0"
                style={{ width: 50, cursor: "pointer" }}
                onClick={() =>
                  setSort(sort === "name-asc" ? "name-desc" : "name-asc")
                }
                tabIndex={-1}
                aria-label="Sort organizations by name"
              >
                {nameSortHeader?.label ?? "Sort"}
              </button>
            </th>
            <th>Org ID</th>
            <th>Timezone</th>
            <th>Currency</th>
            <th>
              App Versions{" "}
              <Badge
                style={{ width: 50, cursor: "pointer" }}
                onClick={() => setSort(nextVersionSort[sort])}
                color="primary"
              >
                {pickLabelForVersionSort(sort)}
              </Badge>
            </th>
            <th>{/* Warnings */}</th>
          </tr>
        </thead>
        <tbody>
          {visibleOrgs.map((o) => {
            return (
              <tr key={o.uid} className={o.disabled && "table-active"}>
                <td>
                  <Link href="/orgs/[org]" as={`/orgs/${o.uid}`}>
                    <a>{o.name}</a>
                  </Link>
                </td>
                <td>
                  <ClipboardLabel iconSize={14} label={`Copy ID for ${o.name}`}>
                    {o.uid}
                  </ClipboardLabel>
                </td>
                <td>{o.timezone}</td>
                <td>{o.currency}</td>
                <td>
                  <VersionBadge appStats={orgVersions.get(o.uid) ?? null} />
                </td>
                <td className="text-right">
                  <WarningPopover org={o.uid} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Container>
  );
}

function compareOrganizations(
  orgA: Organization,
  orgB: Organization,
  sort: SortType,
  orgVersions: Map<string, AppStats>
): number {
  if (sort === "name-asc" || sort === "name-desc") {
    return compareOrganizationNames(
      orgA,
      orgB,
      sort === "name-asc" ? "asc" : "desc"
    );
  }
  const a = orgVersions.get(orgA.uid);
  const b = orgVersions.get(orgB.uid);

  if (!a || !hasValidSortValue(sort, a)) {
    return 1;
  }
  if (!b || !hasValidSortValue(sort, b)) {
    return -1;
  }

  switch (sort) {
    case "android-asc":
      return semverCompare(a.androidAppVersion, b.androidAppVersion);
    case "android-desc":
      return semverCompare(b.androidAppVersion, a.androidAppVersion);
    case "ios-asc":
      return semverCompare(a.iosAppVersion, b.iosAppVersion);
    case "ios-desc":
      return semverCompare(b.iosAppVersion, a.iosAppVersion);
  }
}

function pickLabelForVersionSort(sortType: SortType) {
  switch (sortType) {
    case "name-asc":
    case "name-desc":
      return <>Sort</>;
    case "android-asc":
      return <>And &#8593;</>;
    case "android-desc":
      return <>And &#8595;</>;
    case "ios-asc":
      return <>iOS &#8593;</>;
    case "ios-desc":
      return <>iOS &#8595;</>;
  }
}

function hasValidSortValue(sortType: SortType, version?: AppStats): boolean {
  if (sortType === "android-asc" || sortType === "android-desc") {
    if (!version?.androidAppVersion) {
      return false;
    }
    return true;
  }

  if (sortType === "ios-asc" || sortType === "ios-desc") {
    if (!version?.iosAppVersion) {
      return false;
    }
    return true;
  }
  return false;
}

function semverCompare(a?: string, b?: string): number {
  const aParts = (a ?? "0.0.0").split(".").map(Number);
  const bParts = (b ?? "0.0.0").split(".").map(Number);

  return (
    aParts[0] - bParts[0] || aParts[1] - bParts[1] || aParts[2] - bParts[2] || 0
  );
}

function useCSVBlobURL(orgs: Organization[] | null): string | null {
  const deferred = useDeferredValue(orgs);

  const url = useMemo(() => {
    if (!deferred?.length) {
      return null;
    }

    const csv = stringify(
      [
        [
          "ID",
          "Name",
          "Account Manager",
          "Timezone",
          "Disabled At",
          "Stripe Account ID",
        ],
        ...deferred.map((o) => [
          o.uid,
          o.name,
          o.accountManager,
          o.timezone,
          o.disabled,
          o.stripeAccountId,
        ]),
      ],
      { quoted_string: true }
    );

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    return URL.createObjectURL(blob);
  }, [deferred]);

  useEffect(() => {
    if (!url) return;
    return () => URL.revokeObjectURL(url);
  }, [url]);

  return url;
}
